import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { AgentEvaluatorAssignmentDialog } from "../../components/agents/AgentEvaluatorAssignmentDialog";
import { CreateAgentEvaluatorAssignmentDialog } from "../../components/agents/CreateAgentEvaluatorAssignmentDialog";
import { Badge, Button, Icon, Spinner, Text, View } from "../../components/base";
import { EmptyMessage } from "../../components/EmptyMessage";
import { useAppDispatch, useAppSelector } from "../../redux";
import { agentEvaluatorAssignmentActions, AgentEvaluatorAssignment } from "../../redux/reducers/agentEvaluatorAssignmentSlice";
import { evaluatorActions } from "../../redux/reducers/evaluatorSlice";
import { getSamplingRatePercentage } from "../../utils/samplingRate";

export const AgentEvaluationAssignmentsTab = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const dispatch = useAppDispatch();
  const assignmentState = useAppSelector((state) => state.agentEvaluatorAssignment);
  const evaluatorEntities = useAppSelector((state) => state.evaluator.entities);

  const assignments = useMemo(() => {
    return assignmentState.ids.map((id) => assignmentState.entities[id]).filter((assignment): assignment is AgentEvaluatorAssignment => Boolean(assignment));
  }, [assignmentState.entities, assignmentState.ids]);

  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null);
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  useEffect(() => {
    if (!agentId) {
      return;
    }

    dispatch(agentEvaluatorAssignmentActions.getAssignments({ agentId }));
  }, [agentId, dispatch]);

  useEffect(() => {
    dispatch(evaluatorActions.get({}));
  }, [dispatch]);

  const selectedAssignment = useMemo(() => {
    if (!selectedAssignmentId) {
      return null;
    }
    return assignmentState.entities[selectedAssignmentId] || null;
  }, [assignmentState.entities, selectedAssignmentId]);

  const selectedEvaluator = selectedAssignment ? evaluatorEntities[selectedAssignment.evaluatorId] : null;

  const openAssignmentDialog = (assignment: AgentEvaluatorAssignment) => {
    setSelectedAssignmentId(assignment.id);
    setIsAssignmentDialogOpen(true);
  };

  const closeAssignmentDialog = () => {
    setIsAssignmentDialogOpen(false);
    setSelectedAssignmentId(null);
  };

  const isLoadingAssignments = assignmentState.isLoading;
  const noAssignments = !isLoadingAssignments && assignments.length === 0;

  return (
    <View className="flex flex-col gap-6 p-4">
      <View className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <View className="flex flex-col gap-1">
          <Text className="text-xl font-semibold text-gray-900 dark:text-white">Evaluator Assignments</Text>
          <Text className="text-sm text-gray-500 dark:text-gray-400">Connect evaluators to automatically evaluate this agent's sessions.</Text>
        </View>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          className="btn-primary w-full sm:w-auto"
          isDisabled={!agentId}
          isLoading={assignmentState.isLoading}
        >
          <Icon
            name="add"
            size={20}
            className="mr-1 mt-[1px]"
          />
          Assignment
        </Button>
      </View>

      {isLoadingAssignments ? (
        <View className="flex justify-center py-12">
          <Spinner
            size={32}
            variant="brand"
          />
        </View>
      ) : noAssignments ? (
        <EmptyMessage
          title="No evaluators assigned"
          description="Assign an evaluator to automatically evaluate agent sessions."
          iconName="checklist"
          buttonText={null}
        />
      ) : (
        <View className="border border-gray-200 dark:border-gray-700 rounded-md">
          {assignments.map((assignment, index) => {
            const evaluator = evaluatorEntities[assignment.evaluatorId];
            const rawSampling = assignment.samplingRate;
            const badgeVariant = rawSampling === null || rawSampling === undefined ? "success" : rawSampling === 0 ? "error" : "brand";

            return (
              <View
                key={assignment.id}
                onClick={() => openAssignmentDialog(assignment)}
                className={`py-3 px-4 cursor-pointer transition-colors bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 ${index !== 0 ? "border-t border-gray-200 dark:border-gray-700" : ""}`}
              >
                <View className="flex flex-row justify-between items-start gap-3">
                  <View className="flex flex-col flex-1 min-w-0 gap-2">
                    <View className="flex flex-row gap-2">
                      <Text className="font-semibold text-sm text-gray-900 dark:text-white line-clamp-1">{evaluator?.title || "Evaluator"}</Text>
                    </View>
                    <View className="flex flex-col gap-1 text-xs text-gray-500 dark:text-gray-400 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                      <Badge variant={badgeVariant}>{getSamplingRatePercentage(rawSampling ?? undefined)}</Badge>
                      <Text>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Type:</span>{" "}
                        <span className="text-gray-900 dark:text-white">{evaluator?.evalType || "Unknown"}</span>
                      </Text>
                      <Text>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Target:</span>{" "}
                        <span className="text-gray-900 dark:text-white">{evaluator?.targetFormat || "Unknown"}</span>
                      </Text>
                    </View>
                  </View>
                  <Icon
                    name="chevron-right"
                    size={16}
                    className="text-gray-400 flex-shrink-0 mt-1"
                  />
                </View>
              </View>
            );
          })}
        </View>
      )}

      <AgentEvaluatorAssignmentDialog
        agentId={agentId || ""}
        assignment={selectedAssignment}
        evaluator={selectedEvaluator}
        isOpen={isAssignmentDialogOpen && Boolean(selectedAssignment)}
        onClose={closeAssignmentDialog}
      />
      {agentId ? (
        <CreateAgentEvaluatorAssignmentDialog
          agentId={agentId}
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
        />
      ) : null}
    </View>
  );
};
